import * as React from 'react';
import type {DateRange} from 'react-day-picker';

type UseRangeValueParams = {
  value?: DateRange;
  defaultValue?: DateRange;
  onChange?: (value: DateRange | undefined, name?: string) => void;
  name?: string;
};

const getRangeKey = (range: DateRange | undefined) =>
  `${range?.from?.getTime() ?? ''}-${range?.to?.getTime() ?? ''}`;

export function useRangeValue({
  value: externalValue,
  defaultValue,
  onChange,
  name,
}: UseRangeValueParams) {
  const isControlled = externalValue !== undefined;
  const [draft, setDraft] = React.useState<DateRange | undefined>(
    isControlled ? externalValue : defaultValue,
  );
  const lastExternalKeyRef = React.useRef(getRangeKey(externalValue));

  if (isControlled) {
    const externalKey = getRangeKey(externalValue);
    if (externalKey !== lastExternalKeyRef.current) {
      lastExternalKeyRef.current = externalKey;
      setDraft(externalValue);
    }
  }

  const commit = React.useCallback(
    (next: DateRange | undefined) => {
      setDraft(next);
      if (next === undefined) {
        onChange?.(undefined, name);
        return;
      }
      if (next.from && next.to) onChange?.(next, name);
    },
    [name, onChange],
  );

  return {value: draft, commit};
}
